import { CommonModule } from '@angular/common';
import {
  Component,
  ContentChildren,
  Directive,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { PageEvent, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { Sort, SortDirection, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';

export type TableRow = Record<string, unknown>;
export type TableValue<T> = string | ((row: T) => unknown);
export type TableCellType = 'text' | 'number' | 'date' | 'datetime' | 'boolean' | 'badge' | 'icon';
export type TableTone = 'primary' | 'accent' | 'neutral' | 'warn' | 'success';
export type TableActionAppearance = 'flat' | 'stroked' | 'basic' | 'icon';

export interface TableColumn<T> {
  key: string;
  label: string;
  value?: TableValue<T>;
  secondaryValue?: TableValue<T>;
  type?: TableCellType;
  icon?: string | ((row: T) => string);
  tone?: TableTone | ((row: T) => TableTone);
  emptyText?: string;
  trueLabel?: string;
  falseLabel?: string;
  dateOptions?: Intl.DateTimeFormatOptions;
  numberOptions?: Intl.NumberFormatOptions;
  searchable?: boolean;
  sortable?: boolean;
  align?: 'start' | 'center' | 'end';
  width?: string;
  cellClass?: string | ((row: T) => string);
}

export interface TablePageAction {
  id: string;
  label: string;
  icon?: string;
  appearance?: TableActionAppearance;
  tone?: 'primary' | 'warn';
  disabled?: boolean;
  loading?: boolean;
  tooltip?: string;
}

export interface TableRowAction<T> extends Omit<TablePageAction, 'disabled' | 'loading'> {
  hidden?: (row: T) => boolean;
  disabled?: boolean | ((row: T) => boolean);
  loading?: boolean | ((row: T) => boolean);
}

export interface TableFilterOption {
  label: string;
  value: unknown;
}

export interface TableFilter<T> {
  id: string;
  label: string;
  value?: TableValue<T>;
  options: TableFilterOption[];
  allLabel?: string;
  initialValue?: unknown;
  predicate?: (row: T, selectedValue: unknown) => boolean;
}

export interface TableSummaryItem<T> {
  label: string;
  value: string | number | ((allRows: readonly T[], filteredRows: readonly T[]) => string | number);
  icon?: string;
}

export interface TableEmptyState {
  icon?: string;
  title?: string;
  description?: string;
  actionId?: string;
}

export interface TableInspectorField<T> {
  label: string;
  value: TableValue<T>;
  type?: Exclude<TableCellType, 'icon'>;
  emptyText?: string;
  trueLabel?: string;
  falseLabel?: string;
  dateOptions?: Intl.DateTimeFormatOptions;
  numberOptions?: Intl.NumberFormatOptions;
  monospace?: boolean;
}

export interface TableInspector<T> {
  ariaLabel?: string;
  title: TableValue<T>;
  subtitle?: TableValue<T>;
  description?: TableValue<T>;
  icon?: string | ((row: T) => string);
  tone?: TableTone | ((row: T) => TableTone);
  fields?: TableInspectorField<T>[];
  actions?: readonly TableRowAction<T>[];
  note?: string;
  noteIcon?: string;
}

export interface TablePageActionEvent {
  actionId: string;
}

export interface TableRowActionEvent<T> {
  actionId: string;
  row: T;
}

export interface TableCellContext<T> {
  $implicit: T;
  row: T;
  value: unknown;
  column: TableColumn<T>;
}

/** Rich custom cells can be projected with: <ng-template appTableCell="columnKey">. */
@Directive({
  selector: 'ng-template[appTableCell]',
  standalone: true
})
export class TableCellDirective<T = TableRow> {
  @Input('appTableCell') columnKey = '';

  constructor(readonly template: TemplateRef<TableCellContext<T>>) { }

  static ngTemplateContextGuard<T>(
    _directive: TableCellDirective<T>,
    _context: unknown
  ): _context is TableCellContext<T> {
    return true;
  }
}

let nextTablePageId = 0;

@Component({
  selector: 'app-con-x-table-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule
  ],
  templateUrl: './table-page.component.html',
  styleUrls: ['./table-page.component.scss']
})
export class TablePageComponent<T = TableRow> implements OnChanges {
  @Input() eyebrow = '';
  @Input() eyebrowIcon = '';
  @Input() title = '';
  @Input() description = '';
  @Input() ariaLabel = 'Data table';
  @Input() data: readonly T[] = [];
  @Input() columns: readonly TableColumn<T>[] = [];
  @Input() actions: TablePageAction[] = [];
  @Input() rowActions: readonly TableRowAction<T>[] = [];
  @Input() filters: readonly TableFilter<T>[] = [];
  @Input() summaryItems: readonly TableSummaryItem<T>[] = [];
  @Input() inspector: TableInspector<T> | null = null;
  @Input() selectedRow: T | null = null;
  @Input() rowId: TableValue<T> = 'id';
  @Input() selectableRows = false;
  @Input() searchable = true;
  @Input() searchPlaceholder = 'Search';
  @Input() showSummary = true;
  @Input() countLabel = 'Items';
  @Input() refreshable = false;
  @Input() refreshing = false;
  @Input() refreshTooltip = 'Refresh';
  @Input() loading = false;
  @Input() loadingMessage = 'Loading …';
  @Input() errorMessage = '';
  @Input() errorTitle = 'Data could not be loaded';
  @Input() errorIcon = 'error_outline';
  @Input() retryLabel = 'Try again';
  @Input() emptyState: TableEmptyState = {};
  @Input() noResultsTitle = 'No results';
  @Input() noResultsDescription = 'Change the search or filters.';
  @Input() locale = 'en';
  @Input() pagination = true;
  @Input() serverPagination = false;
  @Input() totalItems = 0;
  @Input() pageIndex = 0;
  @Input() pageSize = 25;
  @Input() pageSizeOptions: readonly number[] = [10, 25, 50, 100];

  @Output() readonly pageAction = new EventEmitter<TablePageActionEvent>();
  @Output() readonly rowAction = new EventEmitter<TableRowActionEvent<T>>();
  @Output() readonly refreshRequested = new EventEmitter<void>();
  @Output() readonly retryRequested = new EventEmitter<void>();
  @Output() readonly selectedRowChange = new EventEmitter<T | null>();
  @Output() readonly searchChange = new EventEmitter<string>();
  @Output() readonly filterChange = new EventEmitter<Readonly<Record<string, unknown>>>();
  @Output() readonly pageChange = new EventEmitter<PageEvent>();

  @ContentChildren(TableCellDirective) private cellTemplates?: QueryList<TableCellDirective<T>>;

  readonly titleId = `generic-table-page-title-${nextTablePageId++}`;
  searchTerm = '';
  filterValues: Record<string, unknown> = {};
  sortActive = '';
  sortDirection: SortDirection = '';
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filters']) this.initialiseFilters();
    if (changes['data']) {
      if (!this.serverPagination) this.pageIndex = 0;
      this.clearMissingSelection();
    }
    if (changes['pageSize']) this.pageIndex = 0;
  }

  get displayedColumns(): string[] {
    const keys = this.columns.map(column => column.key);
    return this.rowActions.length ? [...keys, '__actions'] : keys;
  }

  get filteredRows(): T[] {
    const query = this.searchTerm.trim().toLocaleLowerCase(this.locale);
    const rows = this.data.filter(row => this.matchesSearch(row, query) && this.matchesFilters(row));
    return this.sortRows(rows);
  }

  get visibleRows(): T[] {
    const rows = this.filteredRows;
    if (!this.pagination || this.serverPagination) return rows;
    const start = this.pageIndex * this.pageSize;
    return rows.slice(start, start + this.pageSize);
  }

  get effectiveSummaryItems(): readonly TableSummaryItem<T>[] {
    return this.summaryItems.length ? this.summaryItems : [{ label: this.countLabel, value: this.data.length }];
  }

  get emptyAction(): TablePageAction | undefined {
    if (!this.emptyState.actionId) return undefined;
    return this.actions.find(action => action.id === this.emptyState.actionId);
  }

  setSearchTerm(value: string): void {
    this.searchTerm = value;
    this.pageIndex = 0;
    this.searchChange.emit(value);
  }

  clearSearch(): void {
    this.setSearchTerm('');
  }

  setFilter(filterId: string, value: unknown): void {
    this.filterValues = { ...this.filterValues, [filterId]: value };
    this.pageIndex = 0;
    this.filterChange.emit({ ...this.filterValues });
  }

  handleSort(sort: Sort): void {
    this.sortActive = sort.active;
    this.sortDirection = sort.direction;
    this.pageIndex = 0;
  }

  handlePage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.pageChange.emit(event);
  }

  triggerPageAction(action: TablePageAction): void {
    if (!action.disabled && !action.loading) this.pageAction.emit({ actionId: action.id });
  }

  triggerRowAction(action: TableRowAction<T>, row: T): void {
    if (!this.isRowActionDisabled(action, row) && !this.isRowActionLoading(action, row)) {
      this.rowAction.emit({ actionId: action.id, row });
    }
  }

  selectRow(row: T): void {
    if (!this.canSelectRows()) return;
    this.selectedRow = row;
    this.selectedRowChange.emit(row);
  }

  closeInspector(): void {
    this.selectedRow = null;
    this.selectedRowChange.emit(null);
  }

  isSelected(row: T): boolean {
    if (!this.selectedRow) return false;
    return this.isSameRow(row, this.selectedRow);
  }

  canSelectRows(): boolean {
    return this.selectableRows || this.inspector !== null;
  }

  isColumnSortable(column: TableColumn<T>): boolean {
    return column.sortable ?? column.type !== 'icon';
  }

  isRowActionVisible(action: TableRowAction<T>, row: T): boolean {
    return !action.hidden?.(row);
  }

  isRowActionDisabled(action: TableRowAction<T>, row: T): boolean {
    return typeof action.disabled === 'function' ? action.disabled(row) : !!action.disabled;
  }

  isRowActionLoading(action: TableRowAction<T>, row: T): boolean {
    return typeof action.loading === 'function' ? action.loading(row) : !!action.loading;
  }

  cellTemplate(columnKey: string): TemplateRef<TableCellContext<T>> | null {
    return this.cellTemplates?.find(template => template.columnKey === columnKey)?.template ?? null;
  }

  cellValue(row: T, column: TableColumn<T>): unknown {
    return this.resolveValue(row, column.value ?? column.key);
  }

  secondaryCellValue(row: T, column: TableColumn<T>): string {
    if (!column.secondaryValue) return '';
    return this.formatValue(this.resolveValue(row, column.secondaryValue), column);
  }

  displayCellValue(row: T, column: TableColumn<T>): string {
    return this.formatValue(this.cellValue(row, column), column);
  }

  displayInspectorValue(row: T, field: TableInspectorField<T>): string {
    return this.formatValue(this.resolveValue(row, field.value), field);
  }

  inspectorValue(row: T, value: TableValue<T> | undefined): string {
    if (!value) return '';
    return this.formatValue(this.resolveValue(row, value), {});
  }

  iconFor(row: T, source: string | ((row: T) => string) | undefined): string {
    return typeof source === 'function' ? source(row) : source ?? 'label';
  }

  toneFor(row: T, source: TableTone | ((row: T) => TableTone) | undefined): TableTone {
    return typeof source === 'function' ? source(row) : source ?? 'neutral';
  }

  cellClass(row: T, column: TableColumn<T>): string {
    return typeof column.cellClass === 'function' ? column.cellClass(row) : column.cellClass ?? '';
  }

  summaryValue(item: TableSummaryItem<T>): string | number {
    return typeof item.value === 'function' ? item.value(this.data, this.filteredRows) : item.value;
  }

  trackRow = (index: number, row: T): unknown => this.resolveValue(row, this.rowId) ?? index;

  private initialiseFilters(): void {
    const nextValues: Record<string, unknown> = {};
    for (const filter of this.filters) {
      nextValues[filter.id] = Object.prototype.hasOwnProperty.call(this.filterValues, filter.id)
        ? this.filterValues[filter.id]
        : filter.initialValue ?? null;
    }
    this.filterValues = nextValues;
  }

  private clearMissingSelection(): void {
    if (this.selectedRow && !this.data.some(row => this.isSameRow(row, this.selectedRow!))) this.closeInspector();
  }

  private isSameRow(left: T, right: T): boolean {
    const leftIdentity = this.resolveValue(left, this.rowId);
    const rightIdentity = this.resolveValue(right, this.rowId);
    return leftIdentity === undefined || rightIdentity === undefined
      ? left === right
      : leftIdentity === rightIdentity;
  }

  private matchesSearch(row: T, query: string): boolean {
    if (!query || !this.searchable) return true;
    return this.columns
      .filter(column => column.searchable !== false && column.type !== 'icon')
      .some(column => `${this.displayCellValue(row, column)} ${this.secondaryCellValue(row, column)}`
        .toLocaleLowerCase(this.locale).includes(query));
  }

  private matchesFilters(row: T): boolean {
    return this.filters.every(filter => {
      const selectedValue = this.filterValues[filter.id];
      if (selectedValue === null || selectedValue === undefined) return true;
      if (filter.predicate) return filter.predicate(row, selectedValue);
      return this.resolveValue(row, filter.value ?? filter.id) === selectedValue;
    });
  }

  private sortRows(rows: T[]): T[] {
    if (!this.sortActive || !this.sortDirection) return rows;
    const column = this.columns.find(candidate => candidate.key === this.sortActive);
    if (!column) return rows;
    const direction = this.sortDirection === 'asc' ? 1 : -1;
    return [...rows].sort((left, right) => this.compare(
      this.cellValue(left, column),
      this.cellValue(right, column),
      column.type
    ) * direction);
  }

  private compare(left: unknown, right: unknown, type?: TableCellType): number {
    if (left === right) return 0;
    if (left === null || left === undefined) return 1;
    if (right === null || right === undefined) return -1;
    if (type === 'date' || type === 'datetime' || left instanceof Date || right instanceof Date) {
      return new Date(left as string | number | Date).getTime() - new Date(right as string | number | Date).getTime();
    }
    if (typeof left === 'number' && typeof right === 'number') return left - right;
    return String(left).localeCompare(String(right), this.locale, { numeric: true, sensitivity: 'base' });
  }

  private resolveValue(row: T, source: TableValue<T>): unknown {
    if (typeof source === 'function') return source(row);
    return source.split('.').reduce<unknown>((value, segment) => {
      if (value === null || value === undefined || typeof value !== 'object') return undefined;
      return (value as Record<string, unknown>)[segment];
    }, row);
  }

  private formatValue(
    value: unknown,
    config: Pick<TableColumn<T>, 'type' | 'emptyText' | 'trueLabel' | 'falseLabel' | 'dateOptions' | 'numberOptions'>
  ): string {
    if (value === null || value === undefined || value === '') return config.emptyText ?? '—';
    switch (config.type) {
      case 'number':
        return typeof value === 'number' ? new Intl.NumberFormat(this.locale, config.numberOptions).format(value) : String(value);
      case 'date':
      case 'datetime': {
        const date = value instanceof Date ? value : new Date(value as string | number);
        if (Number.isNaN(date.getTime())) return String(value);
        const defaults: Intl.DateTimeFormatOptions = config.type === 'date'
          ? { day: '2-digit', month: '2-digit', year: 'numeric' }
          : { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' };
        return new Intl.DateTimeFormat(this.locale, config.dateOptions ?? defaults).format(date);
      }
      case 'boolean':
        return value ? config.trueLabel ?? 'Yes' : config.falseLabel ?? 'No';
      default:
        return String(value);
    }
  }
}
